import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";

// Standalone page (no AuthContext, own axios) — must work without a session.
const BASE = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BASE}/api`, withCredentials: false });

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await publicApi.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-clinical-bg" data-testid="forgot-password-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <AfyanexLogo className="h-9 w-auto mb-8" />

          {!sent ? (
            <>
              <h1 className="text-3xl font-heading font-semibold tracking-tight">Forgot password?</h1>
              <p className="mt-2 text-sm text-clinical-subtext">
                Enter the email tied to your Afyanex account and we'll send you a link to reset your password. The link is valid for 60 minutes.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="label-eyebrow mb-1.5 block">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@homecare.app"
                    className="input-clinical"
                    data-testid="forgot-email-input"
                    autoComplete="email"
                  />
                </div>

                {error && (
                  <p className="text-sm text-rose-600" data-testid="forgot-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="btn-primary w-full"
                  data-testid="forgot-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success">
              <MailCheck className="h-12 w-12 text-emerald-500" />
              <h1 className="mt-4 text-2xl font-heading font-semibold tracking-tight">Check your inbox</h1>
              <p className="mt-2 text-sm text-clinical-subtext">
                If an Afyanex account exists for <b>{email}</b>, you'll receive an email with a reset link in the next minute or two. The link expires in 60 minutes.
              </p>
              <p className="mt-3 text-xs text-clinical-subtext">
                Didn't get it? Check your spam folder, or try again in a few minutes.
              </p>
            </div>
          )}

          <Link
            to="/login"
            className="mt-8 inline-flex items-center gap-1.5 text-sm text-clinical-subtext hover:text-clinical-text"
            data-testid="forgot-back-to-login"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
